import React from 'react';

/**
 * Accent-filled button with optional icon glyph and keyboard shortcut chip.
 * Used for page-level primary actions (Compose, New note, etc.).
 */

interface PrimaryButtonProps {
  onClick?: () => void;
  icon?: React.ReactNode;
  shortcut?: string;
  children: React.ReactNode;
  disabled?: boolean;
}

export function PrimaryButton({
  onClick,
  icon,
  shortcut,
  children,
  disabled = false,
}: PrimaryButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '9px 14px',
        background: 'var(--accent)',
        color: 'var(--on-accent)',
        border: '1px solid var(--accent)',
        borderRadius: 8,
        fontFamily: 'var(--font-ui)',
        fontSize: 13.5,
        fontWeight: 500,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'transform 120ms ease, filter 120ms ease',
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.filter = 'brightness(1.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.filter = 'none';
      }}
    >
      {icon && (
        <span aria-hidden style={{ opacity: 0.9, fontSize: 14, display: 'inline-flex' }}>
          {icon}
        </span>
      )}
      <span>{children}</span>
      {shortcut && (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10.5,
            padding: '1px 6px',
            borderRadius: 4,
            border: '1px solid color-mix(in srgb, var(--on-accent) 28%, transparent)',
            color: 'color-mix(in srgb, var(--on-accent) 92%, transparent)',
            letterSpacing: '0.5px',
          }}
        >
          {shortcut}
        </span>
      )}
    </button>
  );
}
